<template>
  <div class="bankid-container">
    <FriedH3>{{ $t('sign-with-mobile-bankid') }}</FriedH3>
    <BankIdStart
      v-if="showStart"
      key="1"
      :bank-id="bankId"
      @setQR="handleSetQR"
      @set-same-device="handleSetSameDevice"
    />
    <BankIdQR v-else-if="showQR" id="bankid-polling-container" key="2" :bank-id="bankId" />
    <BankIdSameDevice v-else-if="showSameDevice" key="3" :bank-id="bankId" :jwt="jwt" />

    <BankIdSigned
      v-else-if="bankId.step === BankIdStep.Signed"
      id="bankid-signed-container"
      key="5"
    />
    <FriedMessage
      v-if="bankId.step === BankIdStep.Failed"
      key="6"
      data-external="eid-error-message"
      :title="$t('something-went-wrong')"
      :type="MessageType.Error"
    >
      {{ bankId.error }}
    </FriedMessage>
  </div>
</template>

<script lang="ts">
import { MessageType } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { computed, defineComponent, onUnmounted, watch } from 'vue';
import { useSigningStore } from '../../../signing/store/signing.store';
import type { BankId } from '../../types/bankid';
import { BankIdSigningType, BankIdStep } from '../../types/enums/bankid-se';
import BankIdQR from './steps/BankIdQR.vue';
import BankIdSameDevice from './steps/BankIdSameDevice.vue';
import BankIdSigned from './steps/BankIdSigned.vue';
import BankIdStart from './steps/BankIdStart.vue';

export default defineComponent({
  components: {
    BankIdStart,
    BankIdSigned,
    BankIdSameDevice,
    BankIdQR,
  },
  props: {
    bankId: {
      type: Object as PropType<BankId>,
      required: true,
    },
    jwt: {
      type: String,
      required: true,
    },
  },
  emits: ['sign'],
  setup(props, { emit }) {
    const signingStore = useSigningStore();

    const isCompleted = () => {
      if (props.bankId.step === BankIdStep.Completed) {
        emit('sign');
      }
    };

    const bankIdError = computed(() => !!props.bankId && props.bankId.error);

    const showStart = computed(
      () =>
        props.bankId.signingType === BankIdSigningType.None &&
        props.bankId.step === BankIdStep.Start
    );

    const showQR = computed(
      () =>
        props.bankId.signingType === BankIdSigningType.QR &&
        props.bankId.step === BankIdStep.Polling
    );

    const showSameDevice = computed(
      () =>
        props.bankId.signingType === BankIdSigningType.SameDevice &&
        [BankIdStep.Start, BankIdStep.Polling].includes(props.bankId.step)
    );

    const handleSetQR = () => {
      signingStore.startBankIdQRMethod();
    };

    const handleSetSameDevice = () => {
      signingStore.startBankIdSameDeviceMethod();
    };

    const handleReset = async () => {
      await signingStore.resetBankId();
    };

    watch(() => props.bankId.step, isCompleted);

    onUnmounted(() => {
      signingStore.resetBankId();
    });

    return {
      BankIdStep,
      isCompleted,
      bankIdError,
      showStart,
      showQR,
      showSameDevice,
      handleSetQR,
      handleSetSameDevice,
      handleReset,
      MessageType,
    };
  },
});
</script>

<style lang="scss" scoped>
.bankid-container {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-150);
}
</style>
